import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Database, RefreshCw, Edit, Trash2, GitMerge, ArrowUpDown, ArrowUp, ArrowDown, Search, X, Replace } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { CanonicalEntity } from '@/services/kbService';
import { QAErrorAlert } from './QAErrorAlert';
import { formatDate, type PaginationState } from './qaUtils';

export interface QACanonicalTabProps {
  entities: CanonicalEntity[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  typeFilter: string;
  searchValue: string;
  entityTypeOptions: string[];
  entityBreakdownEntries: [string, unknown][];
  pagination: PaginationState;
  selectedIds: Set<string>;
  bulkDeleting: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (field: string) => void;
  onTypeFilterChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onPaginationChange: (pagination: PaginationState) => void;
  onRefresh: () => void;
  onEdit: (entity: CanonicalEntity) => void;
  onDelete: (entityId: string) => void;
  onBulkDelete: () => void;
  onBulkRetype?: () => void;
  onMerge: (primaryId: string, secondaryId: string) => void;
  onSelectEntity: (entityId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onClearAllSelections: () => void;
}

const SortableHeader: React.FC<{
  label: string;
  field: string;
  currentSort: string;
  currentOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
  className?: string;
}> = ({ label, field, currentSort, currentOrder, onSort, className }) => (
  <TableHead className={className}>
    <button
      className="flex items-center gap-1 hover:text-foreground transition-colors"
      onClick={() => onSort(field)}
    >
      {label}
      {currentSort === field ? (
        currentOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-30" />
      )}
    </button>
  </TableHead>
);

export const QACanonicalTab: React.FC<QACanonicalTabProps> = ({
  entities,
  totalCount,
  loading,
  error,
  typeFilter,
  searchValue,
  entityTypeOptions,
  entityBreakdownEntries,
  pagination,
  selectedIds,
  bulkDeleting,
  sortBy,
  sortOrder,
  onSortChange,
  onTypeFilterChange,
  onSearchChange,
  onPaginationChange,
  onRefresh,
  onEdit,
  onDelete,
  onBulkDelete,
  onBulkRetype,
  onMerge,
  onSelectEntity,
  onSelectAll,
  onClearAllSelections,
}) => {
  const totalPages = Math.ceil(totalCount / pagination.pageSize);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  // Calculate the range of entities being shown
  const rangeStart = totalCount === 0 ? 0 : (pagination.current - 1) * pagination.pageSize + 1;
  const rangeEnd = Math.min(pagination.current * pagination.pageSize, totalCount);

  // Local search input state for debouncing
  const [localSearch, setLocalSearch] = useState(searchValue);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cross-page selection tracking
  const currentPageIds = useMemo(
    () => new Set(entities.map((e) => e.id)),
    [entities]
  );

  const currentPageSelectedCount = useMemo(
    () => {
      let count = 0;
      currentPageIds.forEach((id) => {
        if (selectedIds.has(id)) count++;
      });
      return count;
    },
    [currentPageIds, selectedIds]
  );

  const otherPageSelectedCount = selectedIds.size - currentPageSelectedCount;
  const isAllCurrentPageSelected = entities.length > 0 && currentPageSelectedCount === entities.length;

  // Sync local search with parent when searchValue changes externally (e.g. tab switch clear)
  useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);

  // Debounced search handler
  const handleSearchInput = useCallback((value: string) => {
    setLocalSearch(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      onSearchChange(value);
    }, 300);
  }, [onSearchChange]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleClearSearch = useCallback(() => {
    setLocalSearch('');
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onSearchChange('');
  }, [onSearchChange]);

  return (
    <>
      {error && (
        <QAErrorAlert
          message="Failed to load canonical entities"
          error={error}
          onRetry={onRefresh}
        />
      )}

      {/* Filters and Actions */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-4">
          {/* Search Input */}
          <div className="relative w-full sm:w-[240px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder="Search entities..."
              value={localSearch}
              onChange={(e) => handleSearchInput(e.target.value)}
              className="pl-8 sm:pl-9 pr-8 h-9 text-xs sm:text-sm"
            />
            {localSearch && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <Select value={typeFilter} onValueChange={onTypeFilterChange}>
            <SelectTrigger className="w-[140px] sm:w-[180px] text-xs sm:text-sm">
              <SelectValue placeholder="All Entity Types" />
            </SelectTrigger>
            <SelectContent className="dark">
              <SelectItem value="all">All Entity Types</SelectItem>
              {entityTypeOptions.map((type) => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            className="text-xs sm:text-sm"
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2 animate-spin" /> : <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />}
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          {selectedIds.size > 0 && (
            <>
            <Separator orientation="vertical" className="h-6 hidden sm:block" />
            <Button
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm"
              onClick={onClearAllSelections}
              title={otherPageSelectedCount > 0 ? `${currentPageSelectedCount} on this page, ${otherPageSelectedCount} on other pages` : undefined}
            >
              {selectedIds.size} selected
              <X className="h-3.5 w-3.5 ml-1" />
            </Button>
            </>
          )}

          {/* Merge Selected Button */}
          <AlertDialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-xs sm:text-sm"
                disabled={selectedIds.size !== 2}
              >
                <GitMerge className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="sm:hidden">{selectedIds.size}</span>
                <span className="hidden sm:inline">Merge Selected ({selectedIds.size})</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="dark bg-background text-foreground max-w-[90vw] sm:max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Merge Entities</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    <p>This will merge two entities into one. The first selected entity will be the <strong>primary</strong> (kept), and the second will be merged into it.</p>
                    {(() => {
                      const ids = Array.from(selectedIds);
                      const primary = entities.find(e => e.id === ids[0]);
                      const secondary = entities.find(e => e.id === ids[1]);
                      return (
                        <div className="mt-2 space-y-1 text-sm">
                          <p><strong>Primary (keep):</strong> {primary?.canonical_value || ids[0]}</p>
                          <p><strong>Merge into primary:</strong> {secondary?.canonical_value || ids[1]}</p>
                        </div>
                      );
                    })()}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    const ids = Array.from(selectedIds);
                    onMerge(ids[0], ids[1]);
                    setMergeDialogOpen(false);
                  }}
                >
                  Merge
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Change Type Button */}
          {onBulkRetype && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm"
              disabled={selectedIds.size === 0}
              onClick={onBulkRetype}
            >
              <Replace className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="sm:hidden">{selectedIds.size}</span>
              <span className="hidden sm:inline">Change Type ({selectedIds.size})</span>
            </Button>
          )}

          {/* Delete Selected Button */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                className="text-xs sm:text-sm"
                disabled={selectedIds.size === 0 || bulkDeleting}
              >
                {bulkDeleting ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 animate-spin" /> : <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />}
                <span className="sm:hidden">{selectedIds.size}</span>
                <span className="hidden sm:inline">Delete Selected ({selectedIds.size})</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="dark bg-background text-foreground max-w-[90vw] sm:max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Selected Entities</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete <strong>{selectedIds.size}</strong> selected entities?
                  {otherPageSelectedCount > 0 && (
                    <> This includes <strong>{otherPageSelectedCount}</strong> entities selected on other pages.</>
                  )}
                  {' '}This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onBulkDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete All
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
      </div>

      {/* Entity Type Breakdown */}
      {entityBreakdownEntries.length > 0 && (
        <div className="mb-4 p-3 sm:p-4 bg-primary/10 border border-primary/30 rounded-lg">
          <p className="text-xs sm:text-sm font-medium text-primary mb-2">Entity Type Distribution</p>
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {entityBreakdownEntries.map(([type, count]) => (
              <Badge key={type} variant="outline" className="bg-primary/15 text-primary border-primary/30 text-[10px] sm:text-xs">
                {type}: {typeof count === 'number' ? count : 0}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <LoadingSpinner message="Loading canonical entities..." fullScreen={false} />
      ) : entities.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 sm:py-12">
          <Database className="h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground mb-3 sm:mb-4" />
          {searchValue ? (
            <>
              <p className="text-sm sm:text-lg font-medium">No entities found</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                No results matching &quot;{searchValue}&quot;. Try a different search term.
              </p>
            </>
          ) : (
            <p className="text-sm sm:text-lg font-medium">No canonical entities found</p>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: Card Layout (scrollable) */}
          <div className="sm:hidden space-y-3 max-h-[70vh] overflow-y-auto">
            {entities.map((entity) => (
              <Card key={entity.id} className="p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedIds.has(entity.id)}
                      onCheckedChange={(checked) => onSelectEntity(entity.id, checked as boolean)}
                    />
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-[10px]">
                      {entity.entity_type || 'Unknown'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onEdit(entity)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="dark bg-background text-foreground max-w-[90vw]">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Entity</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete <strong>{entity.canonical_value}</strong>?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(entity.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <p className="font-medium text-sm truncate">{entity.canonical_value || 'N/A'}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{entity.occurrence_count || 0} occurrences</span>
                    <span>•</span>
                    <span>{Math.round((entity.confidence_score || 0) * 100)}% confidence</span>
                  </div>
                  {entity.aliases && entity.aliases.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {entity.aliases.slice(0, 2).map((alias, idx) => (
                        <Badge key={`alias-${idx}`} variant="secondary" className="text-[10px]">
                          {alias}
                        </Badge>
                      ))}
                      {entity.aliases.length > 2 && (
                        <Badge variant="secondary" className="text-[10px]">
                          +{entity.aliases.length - 2} more
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop: Table Layout with scrollable container and sticky header */}
          <div className="hidden sm:block border rounded-lg">
            <div className="max-h-[70vh] overflow-y-auto overflow-x-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10 shadow-[0_1px_0_0_hsl(var(--border))]">
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={isAllCurrentPageSelected}
                        onCheckedChange={onSelectAll}
                      />
                    </TableHead>
                    <SortableHeader label="Canonical Value" field="canonical_text" currentSort={sortBy} currentOrder={sortOrder} onSort={onSortChange} />
                    <SortableHeader label="Type" field="entity_type" currentSort={sortBy} currentOrder={sortOrder} onSort={onSortChange} className="w-[120px]" />
                    <TableHead>Aliases</TableHead>
                    <SortableHeader label="Occurrences" field="occurrence_count" currentSort={sortBy} currentOrder={sortOrder} onSort={onSortChange} className="w-[100px]" />
                    <SortableHeader label="Confidence" field="confidence_score" currentSort={sortBy} currentOrder={sortOrder} onSort={onSortChange} className="w-[120px]" />
                    <SortableHeader label="Last Modified" field="last_modified" currentSort={sortBy} currentOrder={sortOrder} onSort={onSortChange} className="w-[130px]" />
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entities.map((entity) => (
                    <TableRow key={entity.id} className="hover:bg-muted/50">
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(entity.id)}
                          onCheckedChange={(checked) => onSelectEntity(entity.id, checked as boolean)}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{entity.canonical_value || 'N/A'}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                          {entity.entity_type || 'Unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {entity.aliases && entity.aliases.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {entity.aliases.slice(0, 3).map((alias, idx) => (
                              <Badge key={`alias-${idx}`} variant="secondary" className="text-xs">
                                {alias}
                              </Badge>
                            ))}
                            {entity.aliases.length > 3 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="secondary" className="text-xs">
                                      +{entity.aliases.length - 3} more
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="dark">{entity.aliases.slice(3).join(', ')}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">No aliases</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-primary/15">
                          {entity.occurrence_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={Math.round((entity.confidence_score || 0) * 100)}
                            className="w-16 h-2"
                          />
                          <span className="text-sm">{Math.round((entity.confidence_score || 0) * 100)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{formatDate(entity.last_modified)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => onEdit(entity)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="dark">Edit entity</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <AlertDialog>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-red-400"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                </TooltipTrigger>
                                <TooltipContent className="dark">Delete entity</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <AlertDialogContent className="dark bg-background text-foreground">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Entity</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete <strong>{entity.canonical_value}</strong>?
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => onDelete(entity.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-3 sm:mt-4 gap-2">
            <span className="text-xs sm:text-sm text-muted-foreground">
              {searchValue
                ? `Showing ${rangeStart}-${rangeEnd} of ${totalCount} results`
                : `Showing ${rangeStart}-${rangeEnd} of ${totalCount} entities`
              }
            </span>
            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm"
                onClick={() => onPaginationChange({ ...pagination, current: pagination.current - 1 })}
                disabled={pagination.current === 1}
              >
                Previous
              </Button>
              <span className="text-xs sm:text-sm px-1">
                Page {pagination.current} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm"
                onClick={() => onPaginationChange({ ...pagination, current: pagination.current + 1 })}
                disabled={pagination.current >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
